<template>
  <PageTemplate>

    <Loader v-if="!isLoaded"/>
    <div v-else>

      <!-- An open document has its own back button to the contract list -->
      <BackButton
          v-if="selectedDocumentId === null"
          :label="$t('general.button.back')"
          :to="{ name: 'userList' }"
          marginBottom="5"/>

      <div class="d-flex justify-content-between">
        <h6 class="element-header">
          Verträge - {{ userData?.firstName }} {{ userData?.lastName }} ({{ userData?.engagementTypeTranslation }})
        </h6>
      </div>

      <div class="element-box">
        <UserMenu :userId="userId" :employeeType="userData?.employeeType"/>

        <div class="mt-4">
          <ContractDocumentView
              v-if="selectedDocumentId !== null"
              :key="selectedDocumentId"
              :document-id="selectedDocumentId"
              :is-bsg-side="true"
              @back="selectedDocumentId = null"
              @signed="getContracts"/>

          <template v-else>
            <div class="d-flex justify-content-between align-items-center mb-5">
              <span>
                Status Zugang:
                <span class="badge" :class="userData?.enabled === false ? 'text-bg-warning' : 'text-bg-success'">
                  {{ userData?.enabled === false ? 'Gesperrt bis zur Vertragsunterschrift' : 'Freigeschaltet' }}
                </span>
              </span>
              <button v-if="userData?.engagementType === 'extern'"
                      type="button"
                      class="btn btn-outline-primary mb-3"
                      @click="showCreateModal = true">
                Neuer Vertrag
              </button>
            </div>

            <EmptyState
                v-if="!contracts.length"
                icon="fa-file-signature"
                title="Keine Verträge gefunden"/>

            <ContractList
                v-else
                :contracts="contracts"
                :is-bsg-side="true"
                @open="selectedDocumentId = $event"
                @delete="contractIdToDelete = $event"/>
          </template>
        </div>
      </div>
    </div>

    <Modal
        v-model="showCreateModal"
        title="Neuer Vertrag"
        sizeModal="modal-md"
        button-label="Vertrag erstellen"
        :hide-cancel="false"
        :on-save="createContract">
      <p>Der Vertrag wird mit den aktuellen Daten des Mitarbeiters (Adresse, Honorar) erstellt.</p>
      <SelectVue
          v-model="newContractType"
          :options="contractTypesDropdown"
          :required="true"
          label="Vertrag"/>
    </Modal>

    <ModalConfirm
        :model-value="contractIdToDelete !== null"
        header="Vertrag löschen"
        confirmation-question="Vertrag wirklich löschen? Bereits geleistete Unterschriften gehen verloren."
        :is-processing="isSaving"
        :on-confirm="deleteContract"
        @update:model-value="contractIdToDelete = null"/>
  </PageTemplate>
</template>

<script setup lang="ts">
import {computed, onMounted, ref} from 'vue';
import {useRoute} from 'vue-router';
import userService from '@/api/User';
import userContractService from '@/api/UserContract';
import BackButton from '@/components/button/BackButton.vue';
import EmptyState from '@/components/EmptyState.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/modal/Modal.vue';
import ModalConfirm from '@/components/modal/ModalConfirm.vue';
import PageTemplate from '@/components/PageTemplate.vue';
import SelectVue from '@/components/SelectVue.vue';
import {ErrorToast, SuccessToast, WarningToast} from '@/components/VueToast';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import UserMenu from '@/views/App/User/UserMenu.vue';
import ContractDocumentView from '@/views/App/MyContracts/ContractDocumentView.vue';
import ContractList from '@/views/App/MyContracts/ContractList.vue';

const route = useRoute();
const userId = Number(route.params.id);
const piniaEnumsState = piniaEnumsStore();

const userData = ref<any>(null);
const contracts = ref<any[]>([]);
const isLoaded = ref(false);
const selectedDocumentId = ref<number | null>(null);
const showCreateModal = ref(false);
const newContractType = ref<string | null>(null);
const isSaving = ref(false);
const contractIdToDelete = ref<number | null>(null);

const contractTypesDropdown = computed(() => piniaEnumsState.enums?.userContractTypes || []);

async function getContracts() {
  try {
    const {data} = await userContractService.getUserContracts(userId);
    contracts.value = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
  // BSG signature can complete the contract and enable the user
  await getUser();
}

async function getUser() {
  try {
    const {data} = await userService.getUser(userId);
    userData.value = data?.data?.user;
  } catch (error) {
    await ErrorToast(error);
  }
}

async function createContract() {
  if (!newContractType.value) {
    WarningToast('Bitte wählen Sie den Vertragstyp aus.');
    return;
  }
  if (isSaving.value) {
    return;
  }

  isSaving.value = true;
  try {
    const {data} = await userContractService.createContract(userId, newContractType.value);
    contracts.value = data.data;
    showCreateModal.value = false;
    newContractType.value = null;
    SuccessToast('Vertrag wurde erstellt.');
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSaving.value = false;
  }
}

async function deleteContract() {
  if (contractIdToDelete.value === null || isSaving.value) {
    return;
  }

  isSaving.value = true;
  try {
    const {data} = await userContractService.deleteContract(contractIdToDelete.value);
    contracts.value = data.data;
    SuccessToast('Vertrag wurde gelöscht.');
  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSaving.value = false;
    contractIdToDelete.value = null;
  }
}

onMounted(async () => {
  // Enums are persisted in local storage, reload them if they are older than contract types
  if (!piniaEnumsState.enums?.userContractTypes) {
    await piniaEnumsState.getEnums();
  }

  await getContracts();
  isLoaded.value = true;
});
</script>
